import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Printer, ArrowLeft, ShieldCheck, Lock, QrCode, CalendarClock } from "lucide-react";
import axios from "axios";
import QRCode from "qrcode";
import JsBarcode from "jsbarcode";
import { numberToWords as numWords } from "@/lib/format";
import { FIRM, firmContactLine } from "@/lib/firm";
import { CounterpartyAutocomplete } from "@/components/CounterpartyAutocomplete";

const API = process.env.REACT_APP_BACKEND_URL;

const checkWords = (amount) => {
  const n = Number(amount || 0);
  const dollars = Math.floor(n);
  const cents = Math.round((n - dollars) * 100);
  const words = numWords(dollars).replace(/ Dollars.*$/, "").trim() || "Zero";
  return `${words} and ${String(cents).padStart(2, "0")}/100`;
};

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// MICR E-13B style symbols drawn as inline SVG so they always render correctly
// (Unicode MICR glyphs ⑆⑈ display as broken circled characters without an E-13B font).
const MicrTransit = () => (
  <svg width="15" height="22" viewBox="0 0 15 22" style={{ verticalAlign: "middle" }} aria-hidden>
    <rect x="1.5" y="2" width="3.5" height="18" fill="#0A0A0A" />
    <rect x="10" y="2" width="3.5" height="18" fill="#0A0A0A" />
  </svg>
);
const MicrOnUs = () => (
  <svg width="15" height="22" viewBox="0 0 15 22" style={{ verticalAlign: "middle" }} aria-hidden>
    <rect x="5.5" y="1" width="4" height="14" fill="#0A0A0A" />
    <rect x="1.5" y="15" width="12" height="5.5" fill="#0A0A0A" />
  </svg>
);

// Deterministic 64-char hex e-stamp hash (no crypto dependency needed in-browser).
const stampHash64 = (seed) => {
  let out = "";
  for (let k = 0; k < 8; k++) {
    let h = (0x811c9dc5 ^ (k * 0x9e3779b1)) >>> 0;
    const s = String(seed || "") + ":" + k;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = (h * 0x01000193) >>> 0; }
    out += (h >>> 0).toString(16).padStart(8, "0");
  }
  return out.slice(0, 64);
};

// Seutrade Financial Services notarial e-seal for the back-office Draft Package binder.
const DraftNotarySeal = ({ d }) => {
  const stampedAt = d.dateOfIssue ? new Date(d.dateOfIssue + "T12:00:00Z").toUTCString() : new Date().toUTCString();
  const hash = stampHash64((d.draftNo || "") + "|" + (d.drawerName || "") + "|" + (d.dateOfIssue || ""));
  const commissionId = "STF-NP-" + stampHash64(d.drawerName || "seutrade").slice(0, 8).toUpperCase();
  return (
    <div className="lg-notary" data-testid="draft-notary-seal">
      <div className="lg-notary-ring">
        <div className="lg-notary-top">SEUTRADE FINANCIAL SERVICES</div>
        <div className="lg-notary-mid">NOTARIAL<br />E-SEAL</div>
        <div className="lg-notary-bot">DELAWARE</div>
      </div>
      <div className="lg-notary-body">
        <div className="lg-notary-title">Acknowledged &amp; Notarized — Electronic Notarization</div>
        <div className="lg-notary-line">Seutrade Financial Services Notarial Services · Commissioned Electronic Notary Public</div>
        <div className="lg-notary-line">Commission {commissionId} · New Castle County, Delaware</div>
        <div className="lg-notary-line">Notarized (UTC): <strong>{stampedAt}</strong></div>
        <div className="lg-notary-hash">E-STAMP SHA-256: {hash}</div>
        <div className="lg-notary-note">Executed under the Revised Uniform Law on Notarial Acts (RULONA) &amp; the E-SIGN Act / UETA.</div>
      </div>
    </div>
  );
};


const fmtLongDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
};

const maskSSN = (s) => {
  const digits = String(s || "").replace(/\D/g, "");
  if (!digits) return "___-__-____";
  return "XXX-XX-" + digits.slice(-4);
};

// ABA routing-number check (ANSI mod-10, weights 3-7-1) for domestic deposit.
const abaValid = (routing) => {
  const d = String(routing || "").replace(/\D/g, "");
  if (d.length !== 9) return false;
  const n = d.split("").map(Number);
  const sum = 3 * (n[0] + n[3] + n[6]) + 7 * (n[1] + n[4] + n[7]) + 1 * (n[2] + n[5] + n[8]);
  return sum % 10 === 0;
};

const PURPOSES = {
  finance: "Finance / Funding",
  refinance: "Refinance",
  debt_settlement: "Debt Settlement",
  consolidation: "Debt Consolidation",
};

const CLOSING_DOCS = {
  payoffLetter: "Payoff Letter / Statement",
  certOfSatisfaction: "Certificate / Release of Satisfaction",
  lienRelease: "Lien Release (UCC-3 / DMV)",
  deedRelease: "Deed of Reconveyance / Mortgage Satisfaction",
  titleTransfer: "Title Transfer / Clear Title",
};

const Ctl = ({ label, value, onChange, type = "text", options, testid }) => (
  <label className="flex flex-col gap-1 text-[11px]">
    <span className="uppercase tracking-[0.1em] text-[#8A8A8A] font-semibold">{label}</span>
    {options ? (
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="border border-[#D8D8D0] rounded px-2 py-1.5 text-[12px] bg-white text-black">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    ) : (
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="border border-[#D8D8D0] rounded px-2 py-1.5 text-[12px] bg-white text-black" />
    )}
  </label>
);

const SigGrid = ({ d, leftRole, rightRole }) => (
  <div className="lg-sig-grid">
    <div>
      <div className="lg-sig-party">{d.drawerName}</div>
      <div className="lg-sig-role">{leftRole}</div>
      <div className="lg-sig-cursive">{d.signerName}</div>
      <div className="lg-sig-rule" />
      <div className="lg-sig-cap">By: {d.signerName}</div>
      <div className="lg-sig-cap">Title: {d.signerTitle}</div>
      <div className="lg-sig-cap">Date: {fmtLongDate(d.dateOfIssue)}</div>
    </div>
    <div>
      <div className="lg-sig-party">{d.payTo}</div>
      <div className="lg-sig-role">{rightRole}</div>
      <div className="lg-sig-rule" style={{ marginTop: 36 }} />
      <div className="lg-sig-cap">By: ____________________________</div>
      <div className="lg-sig-cap">Title: ____________________________</div>
      <div className="lg-sig-cap">Date: ____________________________</div>
    </div>
  </div>
);

// E-SIGN Act / UETA authentication seal + SHA-256 hash stamped on every document.
const EsignStamp = ({ auth, docId, compact }) => {
  const shown = compact ? `${(auth.hash || "").slice(0, 40)}…` : auth.hash;
  return (
    <div className={`esign ${compact ? "esign-c" : ""}`}>
      <div className="esign-head"><ShieldCheck size={compact ? 10 : 13} /> ELECTRONICALLY EXECUTED &amp; AUTHENTICATED</div>
      <div className="esign-sub">E-SIGN Act · 15 U.S.C. §7001 et seq. · UETA · Doc {docId}</div>
      <div className="esign-hash">SHA-256: {shown || "computing…"}</div>
      <div className="esign-ts">Auth. timestamp: {auth.ts ? new Date(auth.ts).toUTCString() : "—"}</div>
    </div>
  );
};

export default function DraftPackage() {
  const [d, setD] = useState({
    draftNo: "UTD-2026-001",
    dateOfIssue: "2026-05-15",
    purpose: "finance",
    drawerName: "SEUTRADE FINANCIAL SERVICES SPV SERIES 2026",
    drawerAddress: FIRM.addressLines.join("\n"),
    payTo: "First International Bank",
    beneficiaryAddress: "1500 Financial Center\nMiami, FL 33131\nUSA",
    amount: 250000,
    usanceTerm: "acceptance",
    usanceDays: 90,
    maturityDate: "2026-08-13",
    payAcct: "1234567890",
    payRouting: "FIBKUS33XXX",
    abaRouting: "",
    depositAccount: "",
    acceptingBankName: "",
    collectionTerms: "D/A",
    acceptanceOfficer: "",
    acceptanceTitle: "Authorized Officer",
    acceptanceDate: "",
    reference: "UTD-2026-001",
    commercialAgreementNo: "STF-SPV-2026-001",
    masterAgreementNo: "STF-MFA-2026-001",
    signerName: "John Avery",
    signerTitle: "Authorized Signer",
    micrRouting: "123456789",
    micrAccount: "1234567890",
    micrCheck: "1001",
    governingLaw: "Delaware",
    trustName: "SEUTRADE FINANCIAL SERVICES SPV SERIES 2026",
    trustDate: "2026-01-02",
    trustee: "John Avery",
    uccFileNo: "UCC-2026-STF-0001",
    kycEntityId: "EIN 42-6951310",
    depositHolder: "SEUTRADE FINANCIAL SERVICES SPV SERIES 2026",
    borrowerName: "",
    borrowerSSN: "",
    borrowerAddress: "",
    loanServicer: "",
    loanAccountNo: "",
    payoffAmount: 0,
  });
  const set = (k) => (v) => setD((p) => ({ ...p, [k]: v }));
  const [savedInfo, setSavedInfo] = useState(null); // { saved_at }
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  // Auto-populate the draft + Funding & Security binder from a borrower application
  const { appId } = useParams();
  const [sp, setSp] = useSearchParams();
  const printToken = sp.get("t");
  const lienParam = sp.get("lien");
  useEffect(() => {
    if (!appId) return;
    (async () => {
      try {
        const endpoint = printToken
          ? `${API}/api/borrower/applications/${appId}/public?t=${encodeURIComponent(printToken)}`
          : `${API}/api/borrower/applications/${appId}`;
        const r = await axios.get(endpoint);
        const app = r.data.application || {};
        setUpdatedAt(app.draft_package?.saved_at || app.last_edited_at || app.updated_at || app.created_at || null);
        // If a completed binder was already saved, restore it verbatim so the
        // admin never re-enters fields — this is the official saved copy.
        // Per-lien views (lien=) always generate fresh from that lien's data.
        if (app.draft_package && app.draft_package.data && !lienParam) {
          // Restore the saved copy verbatim, but always refresh the Seutrade
          // drawer/maker address to the current firm address (a firm constant,
          // never deal-specific) so older saved drafts show the correct address.
          setD((p) => ({ ...p, ...app.draft_package.data, drawerAddress: FIRM.addressLines.join("\n") }));
          setSavedInfo({ saved_at: app.draft_package.saved_at, stale: !!app.draft_package.stale });
          // Restore the vaulted redemption/verify codes + medallion so the saved
          // draft still shows its QR and S.T.A.M.P. guarantee after re-open.
          const v = app.draft_package.vault;
          if (v && v.token) {
            try {
              const qr = await QRCode.toDataURL(v.redeemUrl, { margin: 1, width: 320, errorCorrectionLevel: "H" });
              const verifyQr = await QRCode.toDataURL(v.verifyUrl, { margin: 1, width: 320, errorCorrectionLevel: "H" });
              setVault({ vaultId: v.vaultId, token: v.token, redeemUrl: v.redeemUrl, qr, verifyUrl: v.verifyUrl, verifyQr, medallionNo: v.medallionNo, medallionHash: v.medallionHash, reserveBacking: v.reserveBacking });
            } catch { /* QR regen best-effort */ }
          }
          return;
        }
        const b = app.binder || {};
        const liens = Array.isArray(b.mortgages) ? b.mortgages : [];
        const li = (lienParam != null && lienParam !== "") ? Number(lienParam) : (liens.length > 1 ? 0 : null);
        const single = (li != null && !Number.isNaN(li) && liens[li]) ? liens[li] : null;
        const payAmt = single ? Number(single.balance || 0) : Number(b.payoffAmount || b.newPrincipal || 0);
        // ---- Auto-generated transaction identifiers (deterministic per application) ----
        const short = String(app.id || "").replace(/-/g, "").slice(0, 8).toUpperCase();
        const lienSfx = single ? `-L${li + 1}` : "";
        const issueDate = b.closingDate || (app.created_at ? String(app.created_at).slice(0, 10) : new Date().toISOString().slice(0, 10));
        const yr = issueDate.slice(0, 4);
        const md = new Date(issueDate + "T00:00:00"); md.setDate(md.getDate() + 90);
        const maturity = md.toISOString().slice(0, 10);
        setD((p) => ({
          ...p,
          amount: payAmt || p.amount,
          payoffAmount: single ? Number(single.balance || 0) : Number(b.payoffAmount || 0),
          borrowerName: b.borrowerName || "",
          borrowerSSN: b.borrowerSSN || "",
          borrowerAddress: b.borrowerAddress || "",
          loanServicer: single ? (single.lender || "") : (b.currentLender || ""),
          loanAccountNo: single ? (single.account || "") : (b.accountNo || ""),
          payTo: single ? (single.lender || p.payTo) : (b.currentLender || p.payTo),
          beneficiaryAddress: single ? (single.address || p.beneficiaryAddress) : (b.currentLenderAddress || p.beneficiaryAddress),
          draftNo: `UTD-${yr}-${short}${lienSfx}`,
          reference: `UTD-${yr}-${short}${lienSfx}`,
          dateOfIssue: issueDate,
          usanceDays: 90,
          maturityDate: maturity,
          uccFileNo: `UCC-${yr}-STF-${short}`,
          commercialAgreementNo: `STF-SPV-${yr}-${short}${lienSfx}`,
          masterAgreementNo: `STF-MFA-${yr}-${short}`,
          micrAccount: single ? (single.account || p.micrAccount) : (b.accountNo || p.micrAccount),
          lienLabel: single ? `${single.position} Mortgage · ${single.lender || "Lender"}` : "",
          lienIndex: single ? li : null,
          lienCount: liens.length,
        }));
      } catch (e) { /* fall back to manual entry */ }
    })();
  }, [appId, printToken, lienParam]);

  // Persist the completed binder to Loan Servicing & Accounting.
  const saveBinder = async () => {
    if (!appId) { window.alert("Open this draft from a borrower row (Loan Servicing) to save it."); return false; }
    setSaving(true);
    let ok = false;
    try {
      const vaultMeta = vault ? {
        vaultId: vault.vaultId, token: vault.token, redeemUrl: vault.redeemUrl,
        verifyUrl: vault.verifyUrl, medallionNo: vault.medallionNo,
        medallionHash: vault.medallionHash, reserveBacking: vault.reserveBacking,
      } : null;
      const r = await axios.put(`${API}/api/borrower/applications/${appId}/draft-package`, { data: d, vault: vaultMeta });
      setSavedInfo({ saved_at: r.data.saved_at });
      ok = true;
    } catch (e) {
      window.alert("Save failed: " + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
    return ok;
  };

  const [touchingDate, setTouchingDate] = useState(false);
  const touchDate = async () => {
    if (!appId) { window.alert("Open this draft from a borrower row (Loan Servicing) first."); return; }
    const token = localStorage.getItem("th_admin_token");
    setTouchingDate(true);
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const r = await axios.post(`${API}/api/admin/borrower-applications/${appId}/touch-date`, {}, cfg);
      setUpdatedAt(r.data.updated_at);
    } catch (e) { window.alert("Update date failed: " + (e.response?.data?.detail || e.message)); }
    setTouchingDate(false);
  };

  // MICR line — check no. tracks the full draft number; routing uses the
  // domestic 9-digit ABA (SWIFT BIC is NOT valid in the MICR transit field).
  const micrCheck = (String(d.draftNo || "").replace(/\D/g, "") || d.micrCheck || "0001");
  const micrAccount = (d.payAcct || d.micrAccount);
  const abaDigits = String(d.abaRouting || "").replace(/\D/g, "");
  const micrRouting = abaDigits || "000000000";
  const abaOk = abaValid(d.abaRouting);
  const words = useMemo(() => checkWords(d.amount), [d.amount]);
  const purposeLabel = PURPOSES[d.purpose] || "Finance";

  // Live SHA-256 authentication hash over the canonical instrument data.
  const [auth, setAuth] = useState({ hash: "", ts: "", medallion: "" });
  useEffect(() => {
    const canon = JSON.stringify({
      draftNo: d.draftNo, amount: d.amount, drawer: d.drawerName, payTo: d.payTo,
      maturity: d.maturityDate, purpose: d.purpose, signer: d.signerName,
      ca: d.commercialAgreementNo, ucc: d.uccFileNo, trust: d.trustName,
    });
    const ts = new Date().toISOString();
    const run = async () => {
      try {
        const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canon + ts));
        const hex = Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
        const mbuf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode("STAMP-MEDALLION-GUARANTEE|" + hex));
        const mhex = Array.from(new Uint8Array(mbuf)).map((b) => b.toString(16).padStart(2, "0")).join("");
        setAuth({ hash: hex, ts, medallion: mhex });
      } catch { setAuth({ hash: "unavailable", ts, medallion: "" }); }
    };
    run();
  }, [d]);

  const [vault, setVault] = useState(null);
  const [vaulting, setVaulting] = useState(false);
  const [discountRate, setDiscountRate] = useState(6.0);
  const [listStatus, setListStatus] = useState(null);
  const barcodeRef = useRef(null);
  const [closing, setClosing] = useState({ payoffLetter: true, certOfSatisfaction: true, lienRelease: false, deedRelease: false, titleTransfer: false });

  const vaultDraft = async () => {
    setVaulting(true);
    let ok = false;
    try {
      const r = await axios.post(`${API}/api/vault/drafts`, {
        draft_no: d.draftNo, amount: Number(d.amount), drawer_name: d.drawerName,
        pay_to: d.payTo, purpose: d.purpose, maturity_date: d.maturityDate,
        sha256: auth.hash, payload: d,
        accepting_bank: d.acceptingBankName || null,
        acceptance_officer: d.acceptanceOfficer || null,
        acceptance_title: d.acceptanceTitle || null,
        acceptance_date: d.acceptanceDate || null,
      });
      const token = r.data.redemption_token;
      // QR encodes the public, human-readable Withdraw/Redeem page (which then
      // calls the backend redeem endpoint) rather than raw JSON.
      const redeemUrl = `${API}/redeem/${token}`;
      const verifyUrl = `${API}/verify/${token}`;
      const qr = await QRCode.toDataURL(redeemUrl, { margin: 1, width: 320, errorCorrectionLevel: "H" });
      const verifyQr = await QRCode.toDataURL(verifyUrl, { margin: 1, width: 320, errorCorrectionLevel: "H" });
      setVault({
        vaultId: r.data.vault_id, token, redeemUrl, qr, verifyUrl, verifyQr,
        medallionNo: r.data.medallion_no, medallionHash: r.data.medallion_hash,
        reserveBacking: r.data.reserve_backing,
      });
      ok = true;
    } catch (e) {
      window.alert("Vault failed: " + (e.response?.data?.detail || e.message));
    }
    setVaulting(false);
    return ok;
  };

  // One-click: vault the draft (mints the redeemable withdraw QR + medallion),
  // then print once the QR/medallion sheet has rendered onto the instrument.
  const vaultAndPrint = async () => {
    const ok = await vaultDraft();
    if (!ok) return;
    // Persist the completed binder as the official saved copy (best-effort).
    if (appId) { try { await saveBinder(); } catch { /* non-blocking */ } }
    // allow React to paint the QR image + medallion certificate before printing
    await new Promise((res) => setTimeout(res, 700));
    window.print();
  };

  const [clearingBusy, setClearingBusy] = useState("");
  const downloadClearing = async (kind, ext) => {
    setClearingBusy(kind);
    try {
      const body = {
        draft_no: d.draftNo, amount: Number(d.amount),
        drawer_name: d.drawerName, drawer_address: d.drawerAddress,
        pay_to: d.payTo, beneficiary_address: d.beneficiaryAddress,
        date_of_issue: d.dateOfIssue, maturity_date: d.maturityDate,
        aba_routing: d.abaRouting, deposit_account: d.depositAccount || d.payAcct,
        swift_bic: d.payRouting, reference: d.reference,
        accepting_bank: d.acceptingBankName || d.payTo, collection_terms: d.collectionTerms || "D/A",
      };
      const r = await axios.post(`${API}/api/instruments/${kind}`, body, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `UTD_${(d.draftNo || "draft").replace(/\s/g, "_")}_${kind}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e.response?.data instanceof Blob
        ? (await e.response.data.text().then((t) => { try { return JSON.parse(t).detail; } catch { return t; } }))
        : (e.response?.data?.detail || e.message);
      window.alert("Export failed: " + msg);
    }
    setClearingBusy("");
  };

  useEffect(() => {
    if (barcodeRef.current) {
      try { JsBarcode(barcodeRef.current, d.draftNo || "UTD", { format: "CODE128", width: 1.3, height: 30, fontSize: 10, margin: 0 }); } catch {}
    }
  }, [d.draftNo, vault]);

  const listIssuance = async () => {
    setListStatus({ loading: true });
    try {
      const r = await axios.post(`${API}/api/vault/list-issuance`, {
        draft_no: d.draftNo, face_value: Number(d.amount), drawer_name: d.drawerName,
        pay_to: d.payTo, usance_days: Number(d.usanceDays), discount_rate: Number(discountRate),
        execution_date: d.dateOfIssue, maturity_date: d.maturityDate, purpose: d.purpose,
      });
      setListStatus({ ok: true, ...r.data });
    } catch (e) {
      setListStatus({ error: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="binder-print-root min-h-screen bg-[#1a1a14] text-[#ECECE4]">
      <div className="no-print sticky top-0 z-20 bg-[#13130E] border-b border-[#2A2A22]">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-[#9A9A90] hover:text-white flex items-center gap-1.5 text-[12px]" data-testid="draft-back-link">
              <ArrowLeft size={14} /> Back
            </Link>
            <Link to="/" className="text-[#9A9A90] hover:text-white text-[12px]" data-testid="draft-home-link">Home</Link>
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#B8860B]" />
              <span className="font-serif text-lg">Private Negotiable Instrument · Funding &amp; Security Binder</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {appId && (
              <button onClick={saveBinder} disabled={saving} data-testid="draft-save-btn"
                className="flex items-center gap-2 bg-[#1d4ed8] hover:bg-[#1a44bd] disabled:opacity-50 text-white font-semibold text-[12px] uppercase tracking-[0.12em] px-4 py-2 rounded-md">
                <Lock size={15} /> {saving ? "Saving…" : (savedInfo ? "Update Saved Binder" : "Save Completed Binder")}
              </button>
            )}
            {appId && !printToken && (
              <button onClick={touchDate} disabled={touchingDate} data-testid="draft-update-date-btn"
                title="Set the Last Updated date on all of this deal's binders to today"
                className="flex items-center gap-2 border border-[#B8860B] text-[#E4C97A] hover:bg-[#B8860B]/15 disabled:opacity-50 font-semibold text-[12px] uppercase tracking-[0.12em] px-4 py-2 rounded-md">
                <CalendarClock size={15} /> {touchingDate ? "Updating…" : "Update Date"}
              </button>
            )}
            <button onClick={vaultAndPrint} disabled={vaulting} data-testid="draft-vault-print-btn"
              className="flex items-center gap-2 bg-[#0B6B4F] hover:bg-[#0a5a43] disabled:opacity-50 text-white font-semibold text-[12px] uppercase tracking-[0.12em] px-4 py-2 rounded-md">
              <QrCode size={15} /> {vaulting ? "Vaulting…" : "Vault & Print"}
            </button>
            <button onClick={() => window.print()} data-testid="draft-print-btn"
              className="flex items-center gap-2 bg-[#B8860B] hover:bg-[#a0750a] text-[#13130E] font-semibold text-[12px] uppercase tracking-[0.12em] px-4 py-2 rounded-md">
              <Printer size={15} /> Print / Save as PDF
            </button>
          </div>
        </div>
        {appId && savedInfo && (
          <div className="max-w-5xl mx-auto px-6 -mt-1 pb-1" data-testid="draft-saved-badge">
            {savedInfo.stale ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-[#E4B84A]" title="The loan was edited after this draft was saved. Re-save to refresh the archived copy.">
                <ShieldCheck size={13} /> ⚠ Saved copy out of date — loan edited after save · re-save to refresh · {new Date(savedInfo.saved_at).toLocaleString()}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-[#3DBB8E]">
                <ShieldCheck size={13} /> Official copy saved to Loan Servicing &amp; Accounting · {new Date(savedInfo.saved_at).toLocaleString()}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="no-print bg-[#13130E] border-b border-[#2A2A22]">
        <div className="max-w-5xl mx-auto px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="draft-controls">
          <Ctl label="Purpose" value={d.purpose} onChange={set("purpose")}
            options={Object.entries(PURPOSES).map(([v, l]) => ({ v, l }))} />
          <Ctl label="Draft No." value={d.draftNo} onChange={set("draftNo")} />
          <Ctl label="Date of Issue" type="date" value={d.dateOfIssue} onChange={set("dateOfIssue")} />
          <Ctl label="Amount (USD)" type="number" value={d.amount} onChange={(v) => set("amount")(Number(v))} />
          <Ctl label="Drawer / Maker (Seller)" value={d.drawerName} onChange={set("drawerName")} />
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">Find beneficiary / lender / bank (auto-fill)</label>
            <CounterpartyAutocomplete testid="draft-beneficiary-search" onSelect={(r) => {
              set("payTo")(r.name);
              if (r.address) set("beneficiaryAddress")(r.address);
              if (r.swift) set("payRouting")(r.swift);
              if (r.routing) set("abaRouting")(r.routing);
              if (r.account) { set("payAcct")(r.account); set("depositAccount")(r.account); }
            }} />
          </div>
          <Ctl label="Beneficiary / Counterparty (Secured Party)" value={d.payTo} onChange={set("payTo")} />
          <Ctl label="Authorized Signer" value={d.signerName} onChange={set("signerName")} />
          <Ctl label="Signer Title" value={d.signerTitle} onChange={set("signerTitle")} />
          <Ctl label="Usance Term" value={d.usanceTerm} onChange={set("usanceTerm")}
            options={[{ v: "acceptance", l: "After Acceptance" }, { v: "sight", l: "After Sight" }]} />
          <Ctl label="Usance Days" type="number" value={d.usanceDays} onChange={(v) => set("usanceDays")(Number(v))} />
          <Ctl label="Maturity Date" type="date" value={d.maturityDate} onChange={set("maturityDate")} />
          <Ctl label="Account No." value={d.payAcct} onChange={set("payAcct")} />
          <Ctl label="Routing / SWIFT (cross-border)" value={d.payRouting} onChange={set("payRouting")} />
          <label className="flex flex-col gap-1 text-[11px]">
            <span className="uppercase tracking-[0.1em] text-[#8A8A8A] font-semibold">ABA Routing (9-digit, domestic)</span>
            <input value={d.abaRouting} onChange={(e) => set("abaRouting")(e.target.value)} maxLength={9} placeholder="e.g. 021000021"
              className={`border rounded px-2 py-1.5 text-[12px] bg-white text-black ${d.abaRouting ? (abaOk ? "border-[#0B6B4F]" : "border-red-500") : "border-[#D8D8D0]"}`}
              data-testid="aba-routing-input" />
            {d.abaRouting ? (
              <span className={`text-[10px] ${abaOk ? "text-[#3DBB8E]" : "text-red-400"}`} data-testid="aba-routing-status">
                {abaOk ? "✓ Valid ABA (mod-10 check passes)" : "✗ Invalid — fails mod-10 check digit"}
              </span>
            ) : <span className="text-[10px] text-[#8A8A8A]">Required for MICR / X9.37 domestic deposit</span>}
          </label>
          <Ctl label="Deposit Account (payee credits into)" value={d.depositAccount} onChange={set("depositAccount")} testid="deposit-account-input" />
          <Ctl label="Accepting / Paying Bank" value={d.acceptingBankName} onChange={set("acceptingBankName")} testid="accepting-bank-input" />
          <Ctl label="Acceptance Officer" value={d.acceptanceOfficer} onChange={set("acceptanceOfficer")} testid="acceptance-officer-input" />
          <Ctl label="Officer Title" value={d.acceptanceTitle} onChange={set("acceptanceTitle")} testid="acceptance-title-input" />
          <Ctl label="Acceptance Date" type="date" value={d.acceptanceDate} onChange={set("acceptanceDate")} testid="acceptance-date-input" />
          <Ctl label="Commercial Agreement No." value={d.commercialAgreementNo} onChange={set("commercialAgreementNo")} />
          <Ctl label="Master Funding Agreement No." value={d.masterAgreementNo} onChange={set("masterAgreementNo")} />
          <Ctl label="UCC-1 File No." value={d.uccFileNo} onChange={set("uccFileNo")} />
          <Ctl label="Governing Law (State)" value={d.governingLaw} onChange={set("governingLaw")} />
          <Ctl label="SPV Trust Name" value={d.trustName} onChange={set("trustName")} />
          <Ctl label="Trust Date" type="date" value={d.trustDate} onChange={set("trustDate")} />
          <Ctl label="Trustee" value={d.trustee} onChange={set("trustee")} />
          <Ctl label="KYC Entity ID (EIN/Reg)" value={d.kycEntityId} onChange={set("kycEntityId")} />
          <Ctl label="Deposit A/C Holder" value={d.depositHolder} onChange={set("depositHolder")} />
          <Ctl label="Borrower / Obligor Name" value={d.borrowerName} onChange={set("borrowerName")} />
          <Ctl label="Borrower SSN" value={d.borrowerSSN} onChange={set("borrowerSSN")} />
          <Ctl label="Borrower Address" value={d.borrowerAddress} onChange={set("borrowerAddress")} />
          <Ctl label="Loan Servicer / Creditor" value={d.loanServicer} onChange={set("loanServicer")} />
          <Ctl label="Loan / Account No." value={d.loanAccountNo} onChange={set("loanAccountNo")} />
          <Ctl label="Payoff Amount (USD)" type="number" value={d.payoffAmount} onChange={(v) => set("payoffAmount")(Number(v))} />
        </div>
        <div className="max-w-5xl mx-auto px-6 pb-4 flex flex-wrap items-center gap-4">
          <button onClick={vaultDraft} disabled={vaulting} data-testid="vault-draft-btn"
            className="flex items-center gap-2 border border-[#0B6B4F] text-[#0B6B4F] hover:bg-[#0B6B4F]/10 text-[11px] uppercase tracking-[0.12em] px-3 py-2 rounded-md disabled:opacity-50">
            <QrCode size={14} /> {vaulting ? "Vaulting…" : vault ? "Re-Vault & Regenerate Codes" : "Vault Draft & Generate Codes"}
          </button>
          {vault && <span className="text-[11px] text-[#0B6B4F]" data-testid="vault-status">Vaulted · {vault.vaultId} · redeem token issued</span>}
          <div className="flex items-center gap-2 text-[11px] text-[#9A9A90]">
            <span className="uppercase tracking-[0.1em] font-semibold">Discount %/yr</span>
            <input type="number" value={discountRate} onChange={(e) => setDiscountRate(Number(e.target.value))}
              className="w-16 border border-[#D8D8D0] rounded px-2 py-1.5 text-[12px] bg-white text-black" data-testid="discount-rate-input" />
            <button onClick={listIssuance} disabled={listStatus?.loading} data-testid="list-market-btn"
              className="flex items-center gap-2 border border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B]/10 text-[11px] uppercase tracking-[0.12em] px-3 py-2 rounded-md disabled:opacity-50">
              {listStatus?.loading ? "Listing…" : "List on Secondary Market"}
            </button>
            {listStatus?.ok && <span className="text-[#B8860B]" data-testid="list-status">Listed · purchase ${Number(listStatus.purchase_price).toLocaleString()} ({listStatus.discount_pct}% disc)</span>}
            {listStatus?.error && <span className="text-red-400">{listStatus.error}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[11px] text-[#9A9A90]">
            <span className="uppercase tracking-[0.1em] font-semibold">Closing deliverables:</span>
            {Object.entries(CLOSING_DOCS).map(([k, label]) => (
              <label key={k} className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={!!closing[k]} onChange={(e) => setClosing((p) => ({ ...p, [k]: e.target.checked }))} data-testid={`closing-${k}`} /> {label}
              </label>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="clearing-exports">
            <span className="uppercase tracking-[0.1em] font-semibold text-[11px] text-[#9A9A90] mr-1">Bank deposit &amp; clearing:</span>
            <label className="inline-flex items-center gap-1.5 text-[11px] text-[#9DB4E8]" title="Documents against Acceptance (D/A) releases the payoff/lien-release documents against acceptance of the time draft; Documents against Payment (D/P) releases them only against actual payment.">
              <span className="uppercase tracking-[0.08em] text-[#8A8A8A]">Collection:</span>
              <select value={d.collectionTerms || "D/A"} onChange={(e) => set("collectionTerms")(e.target.value)} data-testid="collection-terms-select"
                className="bg-[#0b0f1a] border border-[#0e2a6b] text-[#9DB4E8] rounded px-2 py-1 text-[11px]">
                <option value="D/A">D/A — Documents against Acceptance</option>
                <option value="D/P">D/P — Documents against Payment</option>
              </select>
            </label>
            <button onClick={() => downloadClearing("x937", "x937")} disabled={!!clearingBusy} data-testid="export-x937-btn"
              className="border border-[#0e2a6b] text-[#9DB4E8] hover:bg-[#0e2a6b]/20 text-[11px] uppercase tracking-[0.1em] px-3 py-1.5 rounded-md disabled:opacity-50" title="X9.37 Image Cash Letter (domestic truncated deposit) — needs a valid ABA">
              {clearingBusy === "x937" ? "…" : "X9.37 Cash Letter"}
            </button>
            <button onClick={() => downloadClearing("mt103", "txt")} disabled={!!clearingBusy} data-testid="export-mt103-btn"
              className="border border-[#0e2a6b] text-[#9DB4E8] hover:bg-[#0e2a6b]/20 text-[11px] uppercase tracking-[0.1em] px-3 py-1.5 rounded-md disabled:opacity-50" title="SWIFT MT103 — cross-border credit transfer">
              {clearingBusy === "mt103" ? "…" : "SWIFT MT103"}
            </button>
            <button onClick={() => downloadClearing("mt400", "txt")} disabled={!!clearingBusy} data-testid="export-mt400-btn"
              className="border border-[#0e2a6b] text-[#9DB4E8] hover:bg-[#0e2a6b]/20 text-[11px] uppercase tracking-[0.1em] px-3 py-1.5 rounded-md disabled:opacity-50" title="SWIFT MT400 — advice of payment (documentary collection)">
              {clearingBusy === "mt400" ? "…" : "SWIFT MT400"}
            </button>
            <button onClick={() => downloadClearing("mt412", "txt")} disabled={!!clearingBusy} data-testid="export-mt412-btn"
              className="border border-[#0e2a6b] text-[#9DB4E8] hover:bg-[#0e2a6b]/20 text-[11px] uppercase tracking-[0.1em] px-3 py-1.5 rounded-md disabled:opacity-50" title="SWIFT MT412 — advice of acceptance (documentary collection)">
              {clearingBusy === "mt412" ? "…" : "SWIFT MT412"}
            </button>
            <button onClick={() => downloadClearing("urc522", "txt")} disabled={!!clearingBusy} data-testid="export-urc522-btn"
              className="border border-[#B8860B] text-[#E4C97A] hover:bg-[#B8860B]/15 text-[11px] uppercase tracking-[0.1em] px-3 py-1.5 rounded-md disabled:opacity-50" title="ICC URC 522 documentary-collection cover schedule">
              {clearingBusy === "urc522" ? "…" : "URC 522 Cover Letter"}
            </button>
          </div>
        </div>
      </div>

      {d.lienCount > 1 && (
        <div className="no-print bg-[#0b0f1a] border-b border-[#0e2a6b]/50" data-testid="draft-lien-switcher">
          <div className="max-w-5xl mx-auto px-6 py-2.5 flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#9DB4E8] font-bold">{d.lienCount} mortgages — each funded by its OWN Private Negotiable Instrument:</span>
            {Array.from({ length: d.lienCount }).map((_, i) => (
              <button key={i} onClick={() => { const n = new URLSearchParams(sp); n.set("lien", String(i)); setSp(n); }} data-testid={`draft-lien-tab-${i}`}
                className={`px-3 py-1 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm border ${(d.lienIndex === i) ? "bg-[#0e2a6b] text-white border-[#0e2a6b]" : "bg-[#0f1116] text-[#9DB4E8] border-[#22304f] hover:bg-[#141a2a]"}`}>
                {i === 0 ? "1st Mortgage" : i === 1 ? "2nd Mortgage" : "3rd Mortgage"}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="print-area max-w-5xl mx-auto px-6 py-8 space-y-8">

        {/* ===== Cover ===== */}
        <div className="paper-sheet legal-page" data-testid="binder-cover">
          <div className="lg-wordmark">SEUTRADE FINANCIAL SERVICES</div>
          <div className="lg-firmid" data-testid="draft-firm-identity">{firmContactLine}</div>
          <div className="lg-eyebrow">Commercial Liquidity &amp; Financing Package · Master Funding Agreement</div>
          <h1 className="lg-title">Master Funding &amp; Settlement Agreement — Private Negotiable Instrument Funding Package</h1>
          {d.lienLabel ? (
            <div data-testid="draft-lien-banner" style={{ border: "2px solid #0e2a6b", borderRadius: 6, padding: "8px 12px", margin: "8px 0", fontFamily: "Georgia,'Times New Roman',serif" }}>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "#0e2a6b", fontWeight: 700 }}>Single-Lien Funding File</div>
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>{d.lienLabel}</div>
              <div style={{ fontSize: 11, color: "#444", marginTop: 3 }}>Funds the payoff of <strong>{d.payTo}</strong> (Acct {d.loanAccountNo || "—"}) in the face amount of <strong>USD {fmtMoney(d.amount)}</strong>. One of multiple lien files consolidated under a single SEUTRADE borrower obligation.</div>
            </div>
          ) : null}
          <div className="lg-rule" />
          <table className="lg-table">
            <tbody>
              <tr><td>Draft No.</td><td>{d.draftNo}</td></tr>
              <tr><td>Master Funding Agreement No.</td><td>{d.masterAgreementNo}</td></tr>
              <tr><td>Purpose</td><td>{purposeLabel}</td></tr>
              <tr><td>Drawer / Maker / Seller</td><td>{d.drawerName}</td></tr>
              <tr><td>Beneficiary / Counterparty (Secured Party)</td><td>{d.payTo}</td></tr>
              <tr><td>Face Amount</td><td>USD {fmtMoney(d.amount)} ({words})</td></tr>
              <tr><td>Usance / Maturity</td><td>{d.usanceDays} days after {d.usanceTerm === "sight" ? "sight" : "acceptance"} · {fmtLongDate(d.maturityDate)}</td></tr>
              <tr><td>Authorized Signer</td><td>{d.signerName}, {d.signerTitle}</td></tr>
              <tr><td>Date of Issue</td><td>{fmtLongDate(d.dateOfIssue)}</td></tr>
              {updatedAt && <tr><td>Last Updated</td><td data-testid="draft-updated-date">{fmtLongDate(String(updatedAt).slice(0, 10))}</td></tr>}
            </tbody>
          </table>
          <p className="lg-h">CONTENTS</p>
          <ol className="lg-ol lg-toc">
            <li>Private Negotiable Instrument (Negotiable Instrument — UCC §3-104, Check Format)</li>
            <li>Master Funding Source &amp; Security Agreement (First-Priority Perfected Security Interest)</li>
            <li>UCC-1 Financing Statement (Ready to File — {d.governingLaw} Secretary of State)</li>
            <li>SPV Trust Certificate &amp; Incumbency</li>
            <li>Assignment &amp; Endorsement</li>
            <li>Irrevocable Disbursement Authorization</li>
            <li>KYC / Customer Identification &amp; Sanctions Certification</li>
            <li>Deposit Account Control Agreement (UCC §9-104)</li>
            <li>Deposit Slip &amp; Provisional / Deferred Credit Receipt</li>
            <li>Secondary-Market Purchase, Discount &amp; Sale Authorization</li>
            <li>Financing / Refinance / Debt-Payoff Agreement (Borrower-Executed)</li>
          </ol>
          <p className="lg-note">This is the <strong>Master Funding &amp; Settlement Agreement (No. {d.masterAgreementNo})</strong> governing the entire funding package below; the individual instruments are issued under, and subject to, this master agreement. The package is structured for presentment to, and acceptance by, a bank, accepting institution, or fiscal agent. The Private Negotiable Instrument is <strong>discounted, distributed, and negotiated to additional counterparties only after the accepting / depository bank has approved and accepted it in writing</strong> — it is not funded, and confers no payment, until then.</p>
          <EsignStamp auth={auth} docId="00 · Cover" />
        </div>

        {/* ===== The Private Negotiable Instrument (standard business-check size 8.5 x 3.5) ===== */}
        <div className="paper-sheet check-page" data-testid="usance-draft">
          <div className="utd-frame">
            <div className="utd-watermark">{d.drawerName}</div>
            <div className="utd-strip">
              <span className="utd-strip-main">USANCE TIME DRAFT · PRIVATE NEGOTIABLE INSTRUMENT — DISCOUNT-GRADE</span>
              <span className="utd-strip-sub">NEGOTIABLE INSTRUMENT (UCC §3-104) · TRUE WATERMARK &amp; SECURITY FEATURES</span>
            </div>
            <div className="utd-body">
              <div className="utd-r1">
                <div className="utd-drawer">
                  <strong>{d.drawerName}</strong>
                  {d.drawerAddress.split("\n").map((l, i) => <span key={i} className="utd-sm">{l}</span>)}
                </div>
                <div className="utd-barcenter" data-testid="utd-barcode-top">
                  <svg ref={barcodeRef} className="utd-barcode" data-testid="utd-barcode" />
                  <span className="utd-barlbl">DRAFT NO · CODE-128 · VERIFY</span>
                </div>
                <div className="utd-meta">
                  <div>Draft No. <span className="utd-red">{d.draftNo}</span></div>
                  <div>Date of Issue: <strong>{fmtLongDate(d.dateOfIssue)}</strong></div>
                </div>
              </div>

              <div className="utd-r2">
                <div className="utd-payto">PAY TO THE<br />ORDER OF</div>
                <div className="utd-payline">{d.payTo}</div>
                <div className="utd-amtbox"><span className="utd-dollar">$</span><span className="utd-amt">{fmtMoney(d.amount)}</span></div>
              </div>

              <div className="utd-r3">
                <span className="utd-words">{words}</span>
                <span className="utd-usd">U.S. DOLLARS</span>
              </div>

              <div className="utd-r4">
                <div className="utd-terms">
                  <div><span className="utd-box">{d.usanceTerm === "acceptance" ? "✓" : ""}</span> {d.usanceDays} days after acceptance &nbsp;
                    <span className="utd-box">{d.usanceTerm === "sight" ? "✓" : ""}</span> after sight</div>
                  <div>Maturity: <strong>{fmtLongDate(d.maturityDate)}</strong></div>
                  <div>Payable at: {d.payTo} · Acct {d.payAcct} · {d.payRouting}</div>
                </div>
                <div className="utd-sig">
                  <div className="utd-signature">{d.signerName}</div>
                  <div className="utd-sigrule" />
                  <div className="utd-sigcap"><span>Authorized Signature — Drawer</span><span>{d.drawerName}</span></div>
                </div>
              </div>

              {/* Bottom band: value clause + e-sign seal (left) | bankable acceptance (right) */}
              <div className="utd-bottom">
                <div className="utd-bl">
                  <div className="utd-r5">
                    <strong>ISSUED UNDER MASTER FUNDING &amp; SETTLEMENT AGREEMENT No. {d.masterAgreementNo}.</strong> <strong>UNCONDITIONAL ORDER TO PAY (UCC §3-104):</strong> on the maturity date, pay the fixed sum shown — <strong>FOR VALUE RECEIVED</strong> — Purpose: {purposeLabel}; secured per the Funding Source &amp; Security Agreement. Discount, distribution &amp; negotiation to additional holders are effective <strong>only upon the accepting / depository bank's written acceptance</strong>. Holder's transfer/endorsement is <strong>WITHOUT RECOURSE</strong> (UCC §3-415(b)).
                  </div>
                  <div className="utd-esign">
                    <span className="utd-esign-h">⬢ E-SIGN / UETA AUTHENTICATED</span>
                    <span className="utd-esign-x" title={`Authentication SHA-256: ${auth.hash || ""}`}>15 U.S.C. §7001 · SHA-256 {(auth.hash || "").slice(0, 24)}…</span>
                    <span className="utd-esign-x">{auth.ts ? new Date(auth.ts).toUTCString() : ""}</span>
                    <span className="utd-medallion" data-testid="utd-medallion" title={`S.T.A.M.P. Medallion Signature Guarantee — Securities Transfer Agents Medallion Program · Guarantee No. M-${(auth.medallion || "").slice(0, 6).toUpperCase()} · SHA-256 ${auth.medallion || ""}`}>
                      <span className="utd-med-badge">✹ S.T.A.M.P. MEDALLION GUAR. M-{(auth.medallion || "").slice(0, 6).toUpperCase() || "——————"}</span>
                      <span className="utd-esign-x">Medallion SHA-256 {(auth.medallion || "").slice(0, 20)}… <span className="utd-med-hint">(hover for full)</span></span>
                    </span>
                  </div>
                </div>
                <div className="utd-acceptbox" data-testid="utd-acceptance-box">
                  <div className="utd-acc-h">ACCEPTANCE — PAYEE / ACCEPTING BANK OR FISCAL AGENT</div>
                  {d.acceptingBankName && d.acceptanceOfficer ? (
                    <>
                      <div className="utd-acc-stamp" data-testid="utd-accepted-stamp">✓ ACCEPTED — ENGAGED TO PAY AT MATURITY</div>
                      <div className="utd-acc-note">Accepted and engaged to pay at maturity per the terms hereof (UCC §3-409/§3-410) by <strong>{d.acceptingBankName}</strong>.</div>
                      <div className="utd-acc-officersig">{d.acceptanceOfficer}</div>
                      <div className="utd-acc-sigline" />
                      <div className="utd-acc-cap"><span>Authorized Signature</span><span>{d.acceptingBankName}</span></div>
                      <div className="utd-acc-fields"><span>By: {d.acceptanceOfficer}</span><span>Title: {d.acceptanceTitle || "—"}</span><span>Date: {d.acceptanceDate ? fmtLongDate(d.acceptanceDate) : "—"}</span></div>
                    </>
                  ) : (
                    <>
                      <div className="utd-acc-note">Accepted and engaged to pay at maturity per the terms hereof (UCC §3-409/§3-410).</div>
                      <div className="utd-acc-sigline" />
                      <div className="utd-acc-cap"><span>Authorized Signature</span><span>{d.acceptingBankName || d.payTo}</span></div>
                      <div className="utd-acc-fields"><span>By: __________________</span><span>Title: ____________</span><span>Date: __________</span></div>
                    </>
                  )}
                </div>
              </div>

              {/* MICR line — QR redemption code is embedded INLINE at the left so
                  minting the vault code never pushes the routing/account MICR
                  numbers down or off the fixed-height (3.5in) check. */}
              <div className="utd-micr" data-testid="utd-micr-line">
                {vault && (
                  <span className="utd-qrwrap" data-testid="utd-qr-block" title={`Scan to withdraw from vault · ${vault.redeemUrl}`}>
                    <img className="utd-qr" src={vault.qr} alt="redeem qr" />
                    <span className="utd-qrlbl">SCAN →<br />WITHDRAW<br />TKN {vault.token.slice(0, 8)}…</span>
                  </span>
                )}
                <span className="micr-grp"><MicrOnUs /><span className="micr">{micrCheck}</span><MicrOnUs /></span>
                <span className="micr-grp"><MicrTransit /><span className="micr">{micrRouting}</span><MicrTransit /></span>
                <span className="micr-grp"><span className="micr">{micrAccount}</span><MicrOnUs /></span>
                <Lock size={18} className="utd-lock" />
              </div>
            </div>
          </div>
          <div className="check-note no-print">Sized 8.5″ × 3.5″ — load standard business check stock (check at top of page) and print.</div>
        </div>

        {/* ===== Medallion Guarantee — Verification Certificate (public lookup) ===== */}
        {vault && (
          <div className="paper-sheet legal-page" data-testid="medallion-verify-sheet" style={{ minHeight: "auto" }}>
            <div className="lg-eyebrow">S.T.A.M.P. · Medallion Signature Guarantee</div>
            <h1 className="lg-title">Medallion Guarantee — Verification Certificate</h1>
            <div className="lg-rule" />
            <div className="mgv-grid">
              <div className="mgv-info">
                <table className="lg-table"><tbody>
                  <tr><td>Guarantee No.</td><td><strong>{vault.medallionNo}</strong></td></tr>
                  <tr><td>Program</td><td>Securities Transfer Agents Medallion Program (S.T.A.M.P.)</td></tr>
                  <tr><td>Guarantor</td><td>SEUTRADE FINANCIAL SERVICES · EIN {FIRM.ein} · {FIRM.address} · Bus: {FIRM.phone}</td></tr>
                  <tr><td>Instrument</td><td>Private Negotiable Instrument No. {d.draftNo} · USD {fmtMoney(d.amount)}</td></tr>
                  <tr><td>Vault ID</td><td>{vault.vaultId}</td></tr>
                  {vault.reserveBacking?.backed && (
                    <tr><td>Reserve Backing</td><td><strong style={{ color: "#0B4636" }}>✓ Reserve-Backed</strong> · {vault.reserveBacking.reserve_no} · ratio {(vault.reserveBacking.ratio * 100).toFixed(0)}% (Internal Network Credit Reserve)</td></tr>
                  )}
                  <tr><td>Medallion SHA-256</td><td className="mgv-hash">{vault.medallionHash}</td></tr>
                </tbody></table>
                <p className="lg-note">Scan the Medallion Verification QR (or visit the URL below) to independently confirm this guarantee and the instrument's authenticity. Verification is read-only and does <strong>not</strong> redeem the instrument.</p>
                <div className="mgv-url" data-testid="medallion-verify-url">{vault.verifyUrl}</div>
              </div>
              <div className="mgv-qr" data-testid="medallion-verify-qr">
                <img src={vault.verifyQr} alt="medallion verification qr" />
                <span>SCAN TO VERIFY MEDALLION</span>
              </div>
            </div>
          </div>
        )}


        {/* ===== Funding Source & Security Agreement ===== */}
        <div className="paper-sheet legal-page" data-testid="funding-security-agreement">
          <div className="lg-eyebrow">Funding Source · Perfected Security</div>
          <h1 className="lg-title">Master Funding Source &amp; Security Agreement</h1>
          <div className="lg-rule" />
          <p>This Master Funding Source &amp; Security Agreement (this <em>"Agreement,"</em> forming part of and governed by <strong>Master Funding &amp; Settlement Agreement No. {d.masterAgreementNo}</strong>), dated <strong>{fmtLongDate(d.dateOfIssue)}</strong>, is between <strong>{d.drawerName}</strong> (the <em>"Funding Source," "Drawer," "Maker," "Seller,"</em> and <em>"Debtor"</em>), acting through <strong>{d.trustName}</strong> where applicable, and <strong>{d.payTo}</strong> (the <em>"Beneficiary," "Counterparty,"</em> and <em>"Secured Party"</em>). Purpose: <strong>{purposeLabel}</strong>.</p>
          <ol className="lg-ol">
            <li><span className="lg-ct">Issuance &amp; Funding Source.</span> The Debtor issues the Private Negotiable Instrument (Draft No. {d.draftNo}, face USD {fmtMoney(d.amount)} — {words}) FOR VALUE RECEIVED under Commercial Agreement No. {d.commercialAgreementNo}, supported by the Funding Source's reserves, credit, and the Collateral (the <em>"Funding Source Assets"</em>), for the purpose of {purposeLabel.toLowerCase()}.</li>
            <li><span className="lg-ct">Grant of Security Interest.</span> To secure all obligations under the Draft and this Agreement, the Debtor hereby <strong>grants, pledges, and assigns to the Secured Party a continuing FIRST-PRIORITY security interest</strong> in: (a) the Draft and all underlying instruments, notes, accounts, and contract rights; (b) the Funding Source Assets; and (c) all products, proceeds, substitutions, and replacements thereof (collectively, the <em>"Collateral"</em>).</li>
            <li><span className="lg-ct">Perfection &amp; Priority.</span> The Debtor authorizes the Secured Party to file UCC-1 financing statement(s) (File No. {d.uccFileNo}) and any continuations/amendments, and to take possession or control of the Collateral under <strong>UCC §§ 9-310, 9-313, and 9-314</strong>. The Debtor warrants the Secured Party's interest is <strong>first in priority</strong>, free of all prior liens, claims, set-offs, or encumbrances.</li>
            <li><span className="lg-ct">Authorized Dealings — Discount &amp; (Re)Hypothecation.</span> The Secured Party is irrevocably authorized to <strong>accept, discount, purchase, deposit, pledge, hypothecate, re-hypothecate, margin, lend against, assign, sell, repo, and otherwise negotiate</strong> the Draft and Collateral, in whole or in part, to any bank, accepting institution, or fiscal agent, and to apply proceeds per the Disbursement Authorization. <strong>Distribution and discount of the Draft to additional counterparties are effective only after the accepting / depository bank has approved and accepted the Draft in writing under Master Funding &amp; Settlement Agreement No. {d.masterAgreementNo}.</strong></li>
            <li><span className="lg-ct">Usance, Acceptance &amp; Maturity.</span> The Draft is payable {d.usanceDays} days after {d.usanceTerm === "sight" ? "sight" : "acceptance"}, maturing {fmtLongDate(d.maturityDate)}. Presentment, acceptance, and payment are governed by <strong>UCC §§ 3-409 and 3-410</strong>; acceptance by the Payee/accepting bank creates the engagement to pay at maturity.</li>
            <li><span className="lg-ct">Negotiability &amp; Holder in Due Course.</span> The Draft satisfies UCC § 3-104 and is freely transferable; a transferee taking for value, in good faith, and without notice of defense takes as a <strong>Holder in Due Course (UCC § 3-302)</strong>, free of personal defenses of the Debtor.</li>
            <li><span className="lg-ct">Representations.</span> The Debtor is duly organized and authorized; this Agreement and the Draft are its legal, valid, binding obligations; and all consents required have been obtained.</li>
            <li><span className="lg-ct">Authorized Signer; Governing Law; Entirety.</span> Executed for the Debtor by <strong>{d.signerName}, {d.signerTitle}</strong>. Governed by the laws of the State of <strong>{d.governingLaw}</strong>. This Agreement, the Draft, and the binder documents constitute the entire agreement.</li>
          </ol>
          <SigGrid d={d} leftRole="(Debtor · Funding Source · Drawer/Maker)" rightRole="(Secured Party · Beneficiary / Counterparty)" />
          <DraftNotarySeal d={d} />
          <p className="lg-h">REQUIRED CLOSING DELIVERABLES — Creditor / Counterparty to Issue</p>
          <div className="lg-checklist">
            {Object.entries(CLOSING_DOCS).map(([k, label]) => (
              <div key={k}>{closing[k] ? "☑" : "☐"} {label}</div>
            ))}
          </div>
          <p className="lg-note">Upon acceptance/collection and satisfaction of the obligation, the Creditor/Counterparty shall execute and deliver each checked instrument above (evidencing release, satisfaction, and clear title) within a commercially reasonable time.</p>
          <EsignStamp auth={auth} docId="02 · Funding & Security Agreement" />
        </div>

        {/* ===== UCC-1 Financing Statement (ready to file) ===== */}
        <div className="paper-sheet legal-page" data-testid="ucc1-financing-statement">
          <div className="lg-eyebrow">Perfection · UCC Article 9 · Ready to File</div>
          <h1 className="lg-title">UCC-1 Financing Statement</h1>
          <div className="lg-rule" />
          <p className="lg-note">Standard Form UCC-1 (UCC § 9-521). Prepared for filing with the <strong>{d.governingLaw} Secretary of State</strong> (central filing office for the Debtor's location, UCC §§ 9-301 &amp; 9-501). File No. {d.uccFileNo}.</p>
          <table className="lg-table"><tbody>
            <tr><td style={{ width: "34%" }}>1a. Debtor — Organization Name</td><td><strong>{d.drawerName}</strong>{d.trustName && d.trustName !== d.drawerName ? ` / ${d.trustName}` : ""}</td></tr>
            <tr><td>1b. Debtor Mailing Address</td><td>{d.drawerAddress.replace(/\n/g, ", ")}</td></tr>
            <tr><td>1c. Debtor Type / Jurisdiction</td><td>Registered Organization — Statutory / Common-Law Trust · Jurisdiction of Organization: {d.governingLaw}</td></tr>
            <tr><td>3a. Secured Party — Name</td><td><strong>{d.payTo}</strong></td></tr>
            <tr><td>3b. Secured Party Mailing Address</td><td>{(d.beneficiaryAddress || "").replace(/\n/g, ", ")}</td></tr>
          </tbody></table>
          <p className="lg-h">4. COLLATERAL</p>
          <p>This financing statement covers the following collateral: all of the Debtor's right, title, and interest in and to <strong>Private Negotiable Instrument No. {d.draftNo}</strong> (face USD {fmtMoney(d.amount)}) and all underlying instruments, promissory notes, accounts, chattel paper, contract rights, and general intangibles; the Funding Source Assets pledged under the Master Funding Source &amp; Security Agreement dated {fmtLongDate(d.dateOfIssue)}; the Deposit Account subject to the Deposit Account Control Agreement; and <strong>all products, proceeds, substitutions, and replacements</strong> of the foregoing (collectively, the "Collateral"), securing a <strong>continuing first-priority security interest</strong> in favor of the Secured Party.</p>
          <div className="lg-checklist">
            <div>☑ 5. Check if collateral is held in a Trust</div>
            <div>☐ 6. Debtor is a Transmitting Utility</div>
            <div>☑ 7. Alternative Designation: Consignee / Consignor · Seller / Buyer · Bailee / Bailor · Licensee / Licensor</div>
            <div>☑ 8. Optional Filer Reference Data: {d.uccFileNo} · Commercial Agreement No. {d.commercialAgreementNo}</div>
          </div>
          <p className="lg-h">FILING INSTRUCTIONS</p>
          <ol className="lg-ol">
            <li><span className="lg-ct">Filing Office.</span> File with the <strong>{d.governingLaw} Secretary of State, UCC Division</strong> (central filing). A court filing does <strong>not</strong> perfect this interest — perfection arises only upon acceptance by the Secretary of State (UCC § 9-501).</li>
            <li><span className="lg-ct">Fixture / Real-Property Collateral.</span> If any collateral is or becomes fixtures, as-extracted minerals, or timber, file a fixture filing in the <strong>county land records</strong> where the related real property is recorded.</li>
            <li><span className="lg-ct">Duration &amp; Continuation.</span> Effective five (5) years from filing; file a UCC-3 Continuation within the six (6) months before lapse (UCC § 9-515).</li>
            <li><span className="lg-ct">Authorization to File.</span> The Debtor <strong>authorizes</strong> the Secured Party to file this financing statement and any amendments/continuations (UCC § 9-509).</li>
          </ol>
          <SigGrid d={d} leftRole="(Debtor · Authorizes Filing)" rightRole="(Secured Party · Filer)" />
          <EsignStamp auth={auth} docId="02-A · UCC-1 Financing Statement" />
        </div>

        {/* ===== SPV Trust Certificate ===== */}
        <div className="paper-sheet legal-page" data-testid="spv-trust-certificate">
          <div className="lg-eyebrow">SPV · Certificate of Trust &amp; Incumbency</div>
          <h1 className="lg-title">SPV Trust Certificate</h1>
          <div className="lg-rule" />
          <p>The undersigned certifies, pursuant to applicable trust law and <strong>UCC § 9-503</strong>, that:</p>
          <ol className="lg-ol">
            <li><span className="lg-ct">Trust.</span> <strong>{d.trustName}</strong> (the <em>"Trust"</em>) is a duly organized common-law / statutory trust established <strong>{fmtLongDate(d.trustDate)}</strong>, validly existing under the laws of the State of {d.governingLaw}, and is the issuing/holding vehicle in connection with the Draft.</li>
            <li><span className="lg-ct">Trustee &amp; Authority.</span> <strong>{d.trustee}</strong> serves as Trustee with full power to issue, endorse, assign, pledge, hypothecate, and deliver negotiable instruments and to grant security interests on behalf of the Trust.</li>
            <li><span className="lg-ct">Incumbency.</span> <strong>{d.signerName}</strong> is the duly authorized signer whose specimen signature below binds the Trust and the Funding Source.</li>
            <li><span className="lg-ct">No Revocation.</span> The Trust is in good standing, its powers have not been revoked or limited, and this certificate may be relied upon by any bank or fiscal agent.</li>
          </ol>
          <div className="lg-sig-grid">
            <div>
              <div className="lg-sig-party">{d.trustName}</div>
              <div className="lg-sig-role">(By its Trustee)</div>
              <div className="lg-sig-cursive">{d.trustee}</div>
              <div className="lg-sig-rule" />
              <div className="lg-sig-cap">Trustee: {d.trustee}</div>
              <div className="lg-sig-cap">Date: {fmtLongDate(d.dateOfIssue)}</div>
            </div>
            <div>
              <div className="lg-sig-party">Specimen — Authorized Signer</div>
              <div className="lg-sig-role">&nbsp;</div>
              <div className="lg-sig-cursive">{d.signerName}</div>
              <div className="lg-sig-rule" />
              <div className="lg-sig-cap">{d.signerName}, {d.signerTitle}</div>
            </div>
          </div>
          <EsignStamp auth={auth} docId="03 · SPV Trust Certificate" />
        </div>

        {/* ===== Assignment ===== */}
        <div className="paper-sheet legal-page" data-testid="assignment">
          <div className="lg-eyebrow">Assignment &amp; Endorsement</div>
          <h1 className="lg-title">Assignment of Draft &amp; Underlying Rights</h1>
          <div className="lg-rule" />
          <p>For value received and for the purpose of <strong>{purposeLabel.toLowerCase()}</strong>, <strong>{d.drawerName}</strong> / <strong>{d.trustName}</strong> (<em>"Assignor"</em>) hereby <strong>sells, assigns, transfers, conveys, and endorses</strong> to <strong>{d.payTo}</strong> (<em>"Assignee"</em>), its successors and assigns, all right, title, and interest in and to Private Negotiable Instrument No. {d.draftNo} in the face amount of <strong>USD {fmtMoney(d.amount)}</strong>, together with the underlying obligations, instruments, accounts, proceeds, and all rights of enforcement and collection (the <em>"Assigned Rights"</em>).</p>
          <p>This assignment is made <strong>WITHOUT RECOURSE</strong> except as expressly provided in the Funding Source &amp; Security Agreement, and the Assignee is hereby vested with the rights of a holder, including the right to <strong>discount, pledge, hypothecate, re-hypothecate, assign, and negotiate</strong> the Assigned Rights to any bank or fiscal agent. Endorsement: <em>"Pay to the order of {d.payTo}, without recourse — {d.drawerName}, by {d.signerName}, {d.signerTitle}."</em></p>
          <SigGrid d={d} leftRole="(Assignor)" rightRole="(Assignee · Beneficiary / Counterparty)" />
          <EsignStamp auth={auth} docId="04 · Assignment & Endorsement" />
        </div>

        {/* ===== Disbursement Authorization ===== */}
        <div className="paper-sheet legal-page" data-testid="disbursement-authorization">
          <div className="lg-eyebrow">Irrevocable Instruction</div>
          <h1 className="lg-title">Disbursement Authorization &amp; Payment Instruction</h1>
          <div className="lg-rule" />
          <p>The undersigned <strong>{d.drawerName}</strong> irrevocably authorizes and directs the accepting bank / fiscal agent to apply and disburse the proceeds of Private Negotiable Instrument No. {d.draftNo} (USD {fmtMoney(d.amount)}) as follows, for the purpose of <strong>{purposeLabel}</strong>:</p>
          <table className="lg-table">
            <tbody>
              <tr><td>Beneficiary / Payee</td><td>{d.payTo}</td></tr>
              <tr><td>Beneficiary Address</td><td>{d.beneficiaryAddress.replace(/\n/g, ", ")}</td></tr>
              <tr><td>Account No.</td><td>{d.payAcct}</td></tr>
              <tr><td>Routing / SWIFT</td><td>{d.payRouting}</td></tr>
              <tr><td>Reference</td><td>{d.reference}</td></tr>
              <tr><td>Value Date</td><td>{fmtLongDate(d.maturityDate)}</td></tr>
              <tr><td>Rail</td><td>Fedwire (domestic) / SWIFT MT103 (cross-border)</td></tr>
            </tbody>
          </table>
          <p className="lg-note">This instruction is irrevocable, may be relied upon by any bank or fiscal agent, and survives acceptance, discount, hypothecation, or assignment of the Draft.</p>
          <SigGrid d={d} leftRole="(Maker · Funding Source)" rightRole="(Acknowledged — Beneficiary / Bank)" />
          <EsignStamp auth={auth} docId="05 · Disbursement Authorization" />
        </div>

        {/* ===== KYC ===== */}
        <div className="paper-sheet legal-page" data-testid="kyc-certification">
          <div className="lg-eyebrow">Compliance · CIP / AML</div>
          <h1 className="lg-title">KYC / Customer Identification &amp; Sanctions Certification</h1>
          <div className="lg-rule" />
          <table className="lg-table">
            <tbody>
              <tr><td>Legal Name (Maker / Funding Source)</td><td>{d.drawerName}</td></tr>
              <tr><td>Trust / Vehicle</td><td>{d.trustName} · est. {fmtLongDate(d.trustDate)}</td></tr>
              <tr><td>Registered Address</td><td>{d.drawerAddress.replace(/\n/g, ", ")}</td></tr>
              <tr><td>Entity ID (EIN / Reg. No.)</td><td>{d.kycEntityId}</td></tr>
              <tr><td>Authorized Signer / Control Person</td><td>{d.signerName}, {d.signerTitle}</td></tr>
              <tr><td>Beneficiary / Counterparty</td><td>{d.payTo}</td></tr>
              <tr><td>Source of Value</td><td>Commercial Agreement No. {d.commercialAgreementNo} · {purposeLabel}</td></tr>
            </tbody>
          </table>
          <p>The undersigned certifies that: (i) the identifying information above is true and complete; (ii) the beneficial owners and control persons have been identified consistent with applicable <strong>CIP/KYC and AML (BSA)</strong> requirements; (iii) the funds and instrument are derived from lawful commercial activity; and (iv) no party is a Specially Designated National or otherwise subject to <strong>OFAC</strong> or other applicable sanctions. The undersigned will provide supporting documentation upon a bank's or fiscal agent's request.</p>
          <SigGrid d={d} leftRole="(Certifying Officer · Maker)" rightRole="(Compliance Acknowledgment)" />
          <EsignStamp auth={auth} docId="06 · KYC / Sanctions Certification" />
        </div>

        {/* ===== Deposit Account Control Agreement ===== */}
        <div className="paper-sheet legal-page" data-testid="control-agreement">
          <div className="lg-eyebrow">Perfection by Control · UCC §9-104</div>
          <h1 className="lg-title">Deposit Account Control Agreement</h1>
          <div className="lg-rule" />
          <p>This Deposit Account Control Agreement (this <em>"DACA"</em>), dated <strong>{fmtLongDate(d.dateOfIssue)}</strong>, is among <strong>{d.depositHolder}</strong> (the <em>"Debtor / Account Holder"</em>), <strong>{d.payTo}</strong> (the <em>"Secured Party / Counterparty"</em>), and the depository institution holding the Deposit Account (the <em>"Bank"</em>).</p>
          <table className="lg-table">
            <tbody>
              <tr><td>Deposit Account Holder</td><td>{d.depositHolder}</td></tr>
              <tr><td>Depository Bank</td><td>{d.payTo}</td></tr>
              <tr><td>Account No. / Routing (SWIFT)</td><td>{d.payAcct} · {d.payRouting}</td></tr>
              <tr><td>Secured Obligation</td><td>Private Negotiable Instrument No. {d.draftNo} · USD {fmtMoney(d.amount)}</td></tr>
            </tbody>
          </table>
          <ol className="lg-ol">
            <li><span className="lg-ct">Control.</span> The Bank acknowledges the Secured Party's security interest in the Deposit Account and agrees that it will comply with instructions originated by the Secured Party directing disposition of funds <strong>without further consent of the Debtor</strong>, thereby giving the Secured Party <strong>"control" within the meaning of UCC §§ 9-104 and 9-314</strong> and perfecting a first-priority interest.</li>
            <li><span className="lg-ct">Priority &amp; Subordination.</span> The Bank subordinates and waives any set-off, banker's lien, or security interest in the Deposit Account in favor of the Secured Party, except for returned items and normal account fees.</li>
            <li><span className="lg-ct">Proceeds.</span> All proceeds of the Draft credited to the Deposit Account constitute Collateral and are subject to this DACA and the Funding Source &amp; Security Agreement.</li>
            <li><span className="lg-ct">Governing Law.</span> Governed by the laws of the State of {d.governingLaw}; the Bank's jurisdiction for UCC §9-304 is its chartering jurisdiction.</li>
          </ol>
          <div className="lg-sig-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div>
              <div className="lg-sig-party">{d.depositHolder}</div>
              <div className="lg-sig-role">(Debtor / Account Holder)</div>
              <div className="lg-sig-cursive">{d.signerName}</div>
              <div className="lg-sig-rule" />
              <div className="lg-sig-cap">By: {d.signerName}, {d.signerTitle}</div>
            </div>
            <div>
              <div className="lg-sig-party">{d.payTo}</div>
              <div className="lg-sig-role">(Secured Party &amp; Depository Bank)</div>
              <div className="lg-sig-rule" style={{ marginTop: 36 }} />
              <div className="lg-sig-cap">By: ____________________  Title: __________</div>
              <div className="lg-sig-cap">Date: ____________________</div>
            </div>
          </div>
          <EsignStamp auth={auth} docId="07 · Deposit Account Control Agreement" />
        </div>

        {/* ===== Deposit Slip / Provisional & Deferred Credit Receipt ===== */}
        <div className="paper-sheet legal-page" data-testid="deposit-receipt">
          <div className="lg-eyebrow">Deposit · Provisional &amp; Deferred Credit</div>
          <h1 className="lg-title">Deposit Slip &amp; Credit Receipt</h1>
          <div className="lg-rule" />
          <p>The Bank / accepting institution acknowledges receipt of the following item for deposit and collection, and posts credit as indicated (UCC §4-215; Reg CC availability):</p>
          <table className="lg-table">
            <tbody>
              <tr><td>Date Deposited</td><td>{fmtLongDate(d.dateOfIssue)}</td></tr>
              <tr><td>Depositor / Payee</td><td>{d.payTo}</td></tr>
              <tr><td>Credited Account No.</td><td>{d.payAcct} · {d.payRouting}</td></tr>
              <tr><td>Item Deposited</td><td>Private Negotiable Instrument No. {d.draftNo}</td></tr>
              <tr><td>Face Amount</td><td>USD {fmtMoney(d.amount)} ({words})</td></tr>
              <tr><td>Provisional Credit Posted</td><td>USD {fmtMoney(d.amount)} — subject to collection</td></tr>
              <tr><td>Availability</td><td><strong>Deferred</strong> until acceptance/maturity ({fmtLongDate(d.maturityDate)}) and final settlement</td></tr>
              <tr><td>Status</td><td>Provisional / deferred; firms up as collected funds upon settlement</td></tr>
            </tbody>
          </table>
          <p className="lg-note">Provisional credit is subject to revocation for dishonor or non-collection. Upon collection/settlement, credit becomes final and available. This receipt evidences the Bank's acceptance of the instrument for deposit and collection.</p>
          <div className="lg-sig-grid">
            <div>
              <div className="lg-sig-party">Received &amp; Credited By</div>
              <div className="lg-sig-role">(Bank / Accepting Institution Officer)</div>
              <div className="lg-sig-rule" style={{ marginTop: 30 }} />
              <div className="lg-sig-cap">By: ____________________  Title: __________</div>
              <div className="lg-sig-cap">{d.payTo} · Date: ____________</div>
            </div>
            <div>
              <div className="lg-sig-party">Deposited By</div>
              <div className="lg-sig-role">(Depositor)</div>
              <div className="lg-sig-cursive">{d.signerName}</div>
              <div className="lg-sig-rule" />
              <div className="lg-sig-cap">{d.signerName}, {d.signerTitle}</div>
            </div>
          </div>
          <EsignStamp auth={auth} docId="08 · Deposit Slip & Credit Receipt" />
        </div>

        {/* ===== Secondary-Market Purchase, Discount & Sale Authorization ===== */}
        <div className="paper-sheet legal-page" data-testid="secondary-market-authorization">
          <div className="lg-eyebrow">Secondary Market · Discount &amp; Funding</div>
          <h1 className="lg-title">Purchase, Discount &amp; Sale Authorization</h1>
          <div className="lg-rule" />
          <p>The Debtor <strong>{d.drawerName}</strong> irrevocably authorizes the Secured Party / holder <strong>{d.payTo}</strong> to fund and monetize Private Negotiable Instrument No. {d.draftNo} (USD {fmtMoney(d.amount)}), <strong>after and conditioned upon the accepting / depository bank's written acceptance under Master Funding &amp; Settlement Agreement No. {d.masterAgreementNo}</strong>, as follows:</p>
          <ol className="lg-ol">
            <li><span className="lg-ct">Authorized Dealings.</span> The holder may <strong>purchase, discount, deposit, sell, assign, trade, exchange, pledge, hypothecate, re-hypothecate, and negotiate</strong> the Draft, in whole or in part, in the private secondary market to any bank, accepting institution, money-market investor, or fiscal agent, on such price and terms as it determines.</li>
            <li><span className="lg-ct">Discount &amp; Satisfaction.</span> The Draft is a discount-grade instrument; upon its <strong>sale or discount for value</strong>, the purchase price / collected proceeds are applied to satisfy the Draft and any provisional or deferred amounts credited at the Bank, discharging the corresponding obligations pro tanto.</li>
            <li><span className="lg-ct">Reinvestment of Proceeds.</span> The holder may, <strong>for its own account and book</strong>, invest the cash proceeds of any such sale/discount in U.S. Treasury bills or other Treasury securities at prevailing market yields. Such reinvestment is the holder's own portfolio activity and is <strong>not</strong> a government guarantee of, and does not itself pay, the Draft.</li>
            <li><span className="lg-ct">Good Faith Purchaser.</span> Any purchaser/transferee taking for value, in good faith, and without notice of defense is a <strong>Holder in Due Course (UCC §3-302)</strong>, free of the Debtor's personal defenses.</li>
          </ol>
          <p className="lg-note">Funding is achieved through a bona fide market purchase/discount for value — not through presentment to, or a guarantee by, the U.S. Treasury or any government instrumentality.</p>
          <SigGrid d={d} leftRole="(Debtor / Maker)" rightRole="(Holder / Secured Party — Acknowledged)" />
          <EsignStamp auth={auth} docId="09 · Secondary-Market Authorization" />
        </div>

        {/* ===== Financing / Refinance / Debt-Payoff Agreement (borrower-executed) ===== */}
        <div className="paper-sheet legal-page" data-testid="financing-payoff-agreement">
          <div className="lg-eyebrow">Borrower-Executed · {purposeLabel}</div>
          <h1 className="lg-title">Financing / Refinance &amp; Debt-Payoff Agreement</h1>
          <div className="lg-rule" />
          <p>This Agreement, dated <strong>{fmtLongDate(d.dateOfIssue)}</strong>, is between <strong>{d.drawerName}</strong> (the <em>"Lender / Funding Source"</em>) and the borrower identified below (the <em>"Borrower / Obligor"</em>), for the purpose of <strong>{purposeLabel}</strong> of the obligation described.</p>
          <table className="lg-table">
            <tbody>
              <tr><td>Borrower / Obligor</td><td>{d.borrowerName || "____________________"}</td></tr>
              <tr><td>Social Security No.</td><td>{maskSSN(d.borrowerSSN)}</td></tr>
              <tr><td>Mailing Address</td><td>{d.borrowerAddress || "____________________"}</td></tr>
              <tr><td>Creditor / Loan Servicer</td><td>{d.loanServicer || "____________________"}</td></tr>
              <tr><td>Loan / Account No.</td><td>{d.loanAccountNo || "____________________"}</td></tr>
              <tr><td>Payoff / Financed Amount</td><td>USD {fmtMoney(d.payoffAmount || d.amount)}</td></tr>
              <tr><td>Funding Instrument</td><td>Private Negotiable Instrument No. {d.draftNo}</td></tr>
            </tbody>
          </table>
          <ol className="lg-ol">
            <li><span className="lg-ct">Purpose &amp; Application.</span> The Lender agrees to apply the proceeds of the funding instrument toward the {d.purpose === "refinance" ? "refinancing" : d.purpose === "debt_settlement" ? "settlement" : d.purpose === "consolidation" ? "consolidation" : "financing/payoff"} of the Borrower's obligation to the Creditor/Servicer identified above.</li>
            <li><span className="lg-ct">Borrower Authorization.</span> The Borrower authorizes the Lender and its counterparties to contact the Creditor/Servicer, obtain payoff figures, and remit payment to the loan account identified, and consents to the collection and use of the information above for KYC/CIP and servicing purposes.</li>
            <li><span className="lg-ct">Borrower Representations.</span> The Borrower represents that the information provided is true and complete, and that the Borrower is authorized to enter into this Agreement.</li>
            <li><span className="lg-ct">Repayment / Terms.</span> Repayment terms, rate, and schedule (if any) are as set forth in the accompanying financing schedule or promissory note, which is incorporated by reference.</li>
            <li><span className="lg-ct">Governing Law.</span> Governed by the laws of the State of {d.governingLaw}.</li>
          </ol>
          <div className="lg-sig-grid">
            <div>
              <div className="lg-sig-party">{d.borrowerName || "BORROWER"}</div>
              <div className="lg-sig-role">(Borrower / Obligor)</div>
              <div className="lg-sig-rule" style={{ marginTop: 30 }} />
              <div className="lg-sig-cap">Signature: ____________________</div>
              <div className="lg-sig-cap">Date: ____________  SSN: {maskSSN(d.borrowerSSN)}</div>
            </div>
            <div>
              <div className="lg-sig-party">{d.drawerName}</div>
              <div className="lg-sig-role">(Lender / Funding Source)</div>
              <div className="lg-sig-cursive">{d.signerName}</div>
              <div className="lg-sig-rule" />
              <div className="lg-sig-cap">By: {d.signerName}, {d.signerTitle}</div>
            </div>
          </div>
          <p className="lg-note">SSN is masked on this printed copy for the Borrower's protection; full identifying details are collected and retained separately under the Lender's data-protection and KYC procedures.</p>
          <EsignStamp auth={auth} docId="10 · Financing / Payoff Agreement" />
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: letter; margin: 0; }
          .no-print { display:none !important; }
          html, body { background:#fff !important; }
          .binder-print-root { background:#fff !important; color:#000 !important; min-height:0 !important; }
          .print-area { max-width:none !important; padding:0 !important; margin:0 !important; }
          .print-area > * + * { margin-top:0 !important; }
          .paper-sheet { box-shadow:none !important; margin:0 !important; page-break-after:always; break-after:page; }
          .paper-sheet:last-child { page-break-after:auto; break-after:auto; }
          .check-page { padding:0 !important; }
          .legal-page { padding:0.7in 0.8in !important; }
          .lg-ol > li, .lg-list > li, .lg-toc > li, .lg-checklist, p { break-inside:avoid !important; page-break-inside:avoid !important; }
          .lg-table, .lg-sig-grid, .lg-notary, .esign, .mgv-grid, table, tr { break-inside:avoid !important; page-break-inside:avoid !important; }
          .lg-h, .lg-title, .lg-subtitle { break-after:avoid !important; page-break-after:avoid !important; }
        }
        .paper-sheet { background:#fff; color:#0A0A0A; box-shadow:0 10px 40px rgba(0,0,0,.4); margin:0 auto; }

        /* ---- Check: standard business size 8.5 x 3.5 ---- */
        .check-page { width:8.5in; height:3.5in; overflow:hidden; position:relative; }
        .utd-frame { position:relative; height:100%; border:3px solid #0e2a6b; background:linear-gradient(135deg,#eef1f8,#e6ebf5); display:flex; flex-direction:column; }
        .utd-frame::before { content:""; position:absolute; inset:5px; border:.5px solid #c3cce4;
          background-image:repeating-linear-gradient(45deg, rgba(14,42,107,.04) 0, rgba(14,42,107,.04) 1px, transparent 1px, transparent 7px); pointer-events:none; }
        .utd-watermark { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%) rotate(-7deg); font-size:40px; font-weight:800; color:rgba(14,42,107,.06); white-space:nowrap; pointer-events:none; }
        .utd-strip { background:#0e2a6b; color:#fff; text-align:center; padding:6px 4px 5px; position:relative; z-index:2; border-bottom:3px solid #b8860b; display:flex; flex-direction:column; gap:1px; }
        .utd-strip-main { font-size:11px; letter-spacing:.16em; font-weight:800; }
        .utd-strip-sub { font-size:7px; letter-spacing:.12em; font-weight:600; color:#c9d3ee; }
        .utd-body { position:relative; z-index:2; font-family:Georgia,'Times New Roman',serif; color:#1a1f2e; padding:4px 16px 2px; flex:1; display:flex; flex-direction:column; justify-content:space-between; }
        .utd-sm { display:block; font-size:8px; line-height:1.25; }
        .utd-r1 { display:flex; justify-content:space-between; align-items:flex-start; }
        .utd-drawer strong { font-size:11px; }
        .utd-meta { text-align:right; font-size:9.5px; line-height:1.5; }
        .utd-red { color:#b8101a; font-weight:700; }
        .utd-r2 { display:flex; align-items:flex-end; gap:8px; }
        .utd-payto { font-size:8px; font-weight:700; letter-spacing:.04em; line-height:1.1; }
        .utd-payline { flex:1; font-size:14px; border-bottom:1.3px solid #1a1f2e; padding:0 6px 2px; }
        .utd-amtbox { border:1.5px solid #1a1f2e; padding:3px 8px; display:flex; align-items:center; gap:6px; }
        .utd-dollar { font-size:13px; font-weight:700; }
        .utd-amt { font-size:15px; font-weight:800; }
        .utd-r3 { display:flex; align-items:flex-end; gap:8px; }
        .utd-words { flex:1; font-size:11px; border-bottom:1.3px solid #1a1f2e; padding:0 6px 2px; }
        .utd-usd { font-size:9px; letter-spacing:.08em; font-weight:600; }
        .utd-r4 { display:flex; justify-content:space-between; align-items:flex-end; gap:14px; }
        .utd-terms { font-size:8.5px; line-height:1.5; }
        .utd-box { display:inline-flex; width:9px; height:9px; border:1px solid #1a1f2e; align-items:center; justify-content:center; font-size:8px; font-weight:800; vertical-align:middle; }
        .utd-sig { width:230px; }
        .utd-signature { font-family:'Brush Script MT','Segoe Script',cursive; font-size:20px; color:#0e2a6b; text-align:center; line-height:1; }
        .utd-sigrule { border-bottom:1.2px solid #1a1f2e; margin-top:1px; }
        .utd-sigcap { display:flex; justify-content:space-between; font-size:7px; color:#3a4256; margin-top:2px; }
        .utd-bottom { display:flex; gap:14px; align-items:stretch; }
        .utd-bl { flex:1; display:flex; flex-direction:column; justify-content:space-between; }
        .utd-r5 { font-size:6.8px; line-height:1.28; color:#2a3147; }
        .utd-esign { margin-top:3px; border:1px solid #0b6b4f; border-left:3px solid #0b6b4f; background:rgba(11,107,79,.06); padding:2px 6px; display:flex; flex-direction:column; gap:0; }
        .utd-esign-h { font-size:7.5px; font-weight:800; letter-spacing:.06em; color:#0b6b4f; }
        .utd-esign-x { font-family:'Courier New',monospace; font-size:6.5px; color:#2a3147; }
        .utd-medallion { display:flex; flex-direction:column; gap:0; margin-top:2px; padding-top:2px; border-top:1px dashed #0b6b4f; }
        .utd-med-badge { display:inline-block; align-self:flex-start; font-size:6px; font-weight:900; letter-spacing:.04em; color:#0b6b4f; border:1px solid #0b6b4f; border-radius:999px; padding:0 5px; background:rgba(11,107,79,.1); }
        .utd-med-hint { color:#8a93a7; font-style:italic; }
        .mgv-grid { display:flex; gap:28px; align-items:flex-start; margin-top:8px; }
        .mgv-info { flex:1; }
        .mgv-hash { font-family:'Courier New',monospace; font-size:8.5pt; word-break:break-all; }
        .mgv-url { font-family:'Courier New',monospace; font-size:9pt; color:#0B4636; word-break:break-all; margin-top:8px; border-left:3px solid #0B4636; padding:4px 10px; background:rgba(11,70,54,.05); }
        .mgv-qr { flex:0 0 auto; display:flex; flex-direction:column; align-items:center; gap:6px; }
        .mgv-qr img { width:150px; height:150px; border:1px solid #0B4636; padding:6px; background:#fff; image-rendering:pixelated; }
        .mgv-qr span { font-size:8pt; font-weight:800; letter-spacing:.1em; color:#0B4636; }
        .utd-acceptbox { width:280px; border:1px solid #0e2a6b; padding:4px 7px; display:flex; flex-direction:column; }
        .utd-acc-h { font-size:7.5px; font-weight:800; letter-spacing:.05em; color:#0e2a6b; text-align:center; border-bottom:.5px solid #98a3c2; padding-bottom:2px; }
        .utd-acc-note { font-size:7px; line-height:1.35; margin-top:3px; color:#2a3147; }
        .utd-acc-sigline { border-bottom:1.2px solid #1a1f2e; margin-top:7px; }
        .utd-acc-cap { display:flex; justify-content:space-between; font-size:7px; color:#3a4256; margin-top:2px; }
        .utd-acc-fields { display:flex; justify-content:space-between; font-size:7.5px; margin-top:4px; }
        .utd-acc-stamp { font-size:8px; font-weight:900; letter-spacing:.04em; color:#0b6b4f; border:1px solid #0b6b4f; border-radius:3px; padding:1px 4px; text-align:center; margin-top:3px; background:rgba(11,107,79,.08); }
        .utd-acc-officersig { font-family:'Brush Script MT','Segoe Script',cursive; font-size:12px; color:#0e2a6b; text-align:center; line-height:1; margin-top:2px; }
        .utd-micr { display:flex; align-items:center; gap:20px; background:rgba(255,255,255,.72); padding:1px 10px; border-radius:3px; margin-top:1px; height:26px; box-sizing:border-box; overflow:hidden; }
        .utd-micr .utd-qrwrap { display:flex; align-items:center; gap:4px; margin-right:4px; max-height:24px; }
        .utd-micr .utd-qr { width:22px; height:22px; image-rendering:pixelated; }
        .utd-micr .utd-qrlbl { font-size:5.5px; font-weight:800; color:#0e2a6b; letter-spacing:.02em; line-height:1.2; text-align:left; white-space:nowrap; }
        .micr-grp { display:inline-flex; align-items:center; gap:6px; }
        .micr { font-family:'Courier New',monospace; font-weight:700; font-size:16px; letter-spacing:.18em; color:#0A0A0A; }
        .utd-barcenter { display:flex; flex-direction:column; align-items:center; justify-content:flex-start; flex:0 0 auto; padding-top:2px; }
        .utd-barcenter .utd-barcode { height:32px; }
        .utd-barcenter .utd-barlbl { font-size:6.5px; letter-spacing:.14em; color:#334; margin-top:2px; }
        .lg-notary { display:flex; gap:16px; align-items:flex-start; margin:20px 0 6px; border:1.5px solid #7A1420; border-radius:6px; padding:12px 14px; background:rgba(122,20,32,.04); page-break-inside:avoid; }
        .lg-notary-ring { flex-shrink:0; width:104px; height:104px; border-radius:50%; border:3px double #7A1420; color:#7A1420; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; box-shadow:inset 0 0 0 2px rgba(122,20,32,.25); }
        .lg-notary-top { font-size:6.5px; font-weight:800; letter-spacing:.06em; }
        .lg-notary-mid { font-size:11px; font-weight:800; line-height:1.05; margin:4px 0; letter-spacing:.04em; }
        .lg-notary-bot { font-size:6.5px; font-weight:700; letter-spacing:.1em; }
        .lg-notary-body { flex:1; }
        .lg-notary-title { font-size:10pt; font-weight:800; color:#7A1420; letter-spacing:.02em; }
        .lg-notary-line { font-size:9pt; color:#222; margin-top:3px; }
        .lg-notary-hash { font-family:'Courier New',monospace; font-size:7.5pt; color:#111; word-break:break-all; margin-top:4px; }
        .lg-notary-note { font-size:8pt; font-style:italic; color:#555; margin-top:5px; }
        .utd-lock { margin-left:auto; color:#1a1f2e; }
        .check-note { margin-top:8px; font-size:11px; color:#9A9A90; text-align:center; }

        /* ---- Legal pages ---- */
        .legal-page { width:8.5in; min-height:11in; padding:0.7in 0.8in; font-family:Georgia,'Times New Roman',serif; font-size:11pt; line-height:1.5; color:#111; }
        .lg-eyebrow { font-size:9pt; letter-spacing:.16em; text-transform:uppercase; color:#0B4636; font-weight:700; }
        .lg-title { font-size:20pt; font-weight:700; margin:6px 0 0; line-height:1.15; }
        .lg-rule { height:2px; background:#0B4636; margin:12px 0 16px; }
        .lg-h { font-weight:700; letter-spacing:.06em; margin:16px 0 6px; }
        .lg-note { font-size:10pt; font-style:italic; color:#444; margin-top:14px; }
        .lg-ol { padding-left:18px; }
        .lg-ol > li { margin-bottom:9px; }
        .lg-toc > li { margin-bottom:4px; }
        .lg-ct { font-weight:700; }
        .lg-sig-grid { display:grid; grid-template-columns:1fr 1fr; gap:40px; margin-top:24px; }
        .lg-sig-party { font-weight:700; text-transform:uppercase; font-size:10pt; letter-spacing:.04em; }
        .lg-sig-role { font-size:9.5pt; color:#555; margin-bottom:6px; }
        .lg-sig-cursive { font-family:'Brush Script MT','Segoe Script',cursive; font-size:24px; color:#0B4636; }
        .lg-sig-rule { border-bottom:1.2px solid #111; margin:2px 0 6px; }
        .lg-sig-cap { font-size:10pt; }
        .lg-table { width:100%; border-collapse:collapse; margin-top:8px; font-size:10.5pt; }
        .lg-table td { border:1px solid #d8d8d0; padding:6px 9px; }
        .lg-table td:first-child { background:#f6f6f1; font-weight:600; width:42%; }

        /* ---- E-SIGN seal (legal pages) ---- */
        .esign { margin-top:22px; border:1px solid #0B4636; border-left:4px solid #0B4636; background:rgba(11,70,54,.05); padding:8px 12px; }
        .esign-head { font-size:9pt; font-weight:800; letter-spacing:.06em; color:#0B4636; display:flex; align-items:center; gap:6px; }
        .esign-sub { font-size:8pt; color:#3a4256; margin-top:2px; }
        .esign-hash { font-family:'Courier New',monospace; font-size:7.5pt; color:#111; word-break:break-all; margin-top:3px; }
        .esign-ts { font-size:8pt; color:#3a4256; margin-top:2px; }

        /* ---- Vault codes on the draft ---- */
        /* Fixed-height row so minting the QR never pushes the MICR line down. */
        .utd-vaultrow { display:flex; align-items:center; justify-content:center; gap:12px; min-height:62px; }
        .utd-barwrap { display:flex; flex-direction:column; }
        .utd-barcode { height:30px; }
        .utd-barlbl { font-size:6.5px; font-weight:800; color:#0e2a6b; letter-spacing:.12em; margin-top:2px; }
        .utd-vault-ph { font-size:7.5px; color:#6b7280; font-style:italic; max-width:150px; text-align:center; }
        .utd-qrwrap { display:flex; align-items:center; gap:8px; }
        .utd-qr { width:60px; height:60px; image-rendering:pixelated; }
        .utd-qrlbl { font-size:6.5px; font-weight:800; color:#0e2a6b; letter-spacing:.03em; line-height:1.25; max-width:120px; }

        /* ---- Branding + checklist ---- */
        .lg-wordmark { font-family:Georgia,'Times New Roman',serif; font-size:15pt; font-weight:800; letter-spacing:.28em; color:#0B4636; margin-bottom:4px; }
        .lg-firmid { font-size:8.5pt; color:#555; letter-spacing:.02em; margin-bottom:8px; }
        .lg-checklist { font-size:10.5pt; line-height:1.9; margin-top:4px; }
      `}</style>
    </div>
  );
}
